import { motion } from "framer-motion";
import { CalendarBlank, MapPin, UsersThree, Ticket, CheckCircle, ArrowRight, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";
import { COURSE_INFO, STEPS, FACULTY } from "../data/program";
import { generateProgramFlyer } from "../lib/flyer";

const OR_IMG =
  "https://images.unsplash.com/photo-1579154491915-611e891d3a5b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxzdXJnZXJ5JTIwb3BlcmF0aW5nJTIwcm9vbXxlbnwwfHx8fDE3ODU0NTMxNTR8MA&ixlib=rb-4.1.0&q=85";

export default function Program() {
  const { t } = useLang();

  const downloadFlyer = () => {
    try {
      generateProgramFlyer();
      toast.success("Program downloaded");
    } catch (e) {
      console.error("[flyer] error", e);
      toast.error("Could not generate PDF");
    }
  };

  return (
    <div data-testid="program-page" className="min-h-screen">
      {/* Header */}
      <section className="relative overflow-hidden px-6 pt-36 pb-16 md:px-10 grid-lines">
        <div className="absolute -left-40 top-10 h-[520px] w-[520px] glow-cyan" />
        <div className="mx-auto max-w-[1600px]">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="font-mono text-xs uppercase tracking-[0.3em] text-cyan">
            — {t("hero_kicker")}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-4 font-head text-5xl font-black uppercase tracking-tighter text-white md:text-7xl lg:text-[6rem]"
          >
            {t("program_title")}
          </motion.h1>
          <p className="mt-6 max-w-2xl font-body text-base leading-relaxed text-slate-300 md:text-lg">{t("program_sub")}</p>
          <p className="mt-4 max-w-2xl font-mono text-[11px] uppercase tracking-[0.15em] text-cyan/80">{COURSE_INFO.organized_by}</p>
          <button
            data-testid="download-program-btn"
            onClick={downloadFlyer}
            className="group mt-8 inline-flex items-center gap-3 bg-cyan px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink-900 transition-colors duration-300 hover:bg-white"
          >
            <DownloadSimple size={16} weight="bold" className="transition-transform group-hover:translate-y-0.5" />
            {t("download_program")}
          </button>
        </div>
      </section>

      {/* Steps */}
      <section className="mx-auto max-w-[1600px] px-6 pb-8 md:px-10">
        {STEPS.map((step, i) => (
          <motion.div
            key={step.n}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            data-testid={`program-step-${step.n}`}
            className="grid grid-cols-1 gap-8 border-t border-white/10 py-14 lg:grid-cols-12"
          >
            <div className="lg:col-span-4">
              <div className="sticky top-28">
                <div className="font-head text-7xl font-black text-transparent md:text-8xl" style={{ WebkitTextStroke: "1.5px rgba(0,240,255,0.35)" }}>
                  {step.n}
                </div>
                <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-cyan">{t("step")} {step.n}</p>
                <h2 className="mt-2 font-head text-2xl font-bold uppercase leading-tight tracking-tight text-white md:text-3xl">
                  {step.focus}
                </h2>
                <div className="mt-4 flex items-center gap-2 border border-cyan/30 bg-cyan/5 px-3 py-2">
                  <CalendarBlank size={16} className="text-cyan" />
                  <span className="font-mono text-xs uppercase tracking-widest text-slate-200">{step.dates}</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-8">
              <div className="flex flex-col divide-y divide-white/5">
                {step.sessions.map((s, idx) => (
                  <div key={idx} className="group flex items-start justify-between gap-6 py-4 transition-colors hover:bg-white/[0.02]">
                    <p className="font-body text-base leading-snug text-slate-200 transition-colors group-hover:text-white">{s.t}</p>
                    <span className="shrink-0 whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.15em] text-cyan/70">{s.s}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        ))}
      </section>

      {/* Directors & Committee */}
      <section className="mx-auto max-w-[1600px] px-6 py-16 md:px-10">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <InfoBlock icon={UsersThree} title={t("course_directors")} items={COURSE_INFO.directors} />
          <InfoBlock icon={UsersThree} title={t("scientific_committee")} items={COURSE_INFO.committee} />
          <InfoBlock icon={CheckCircle} title={t("patronage")} items={COURSE_INFO.patrons} />
        </div>
      </section>

      {/* Faculty marquee-ish grid */}
      <section className="mx-auto max-w-[1600px] px-6 py-12 md:px-10">
        <h2 className="mb-8 font-head text-3xl font-bold uppercase tracking-tight text-white md:text-4xl">{t("faculty")}</h2>
        <div className="flex flex-wrap gap-2">
          {FACULTY.map((f) => (
            <span key={f} className="border border-white/10 bg-ink-700/40 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.1em] text-slate-300 transition-colors hover:border-cyan/40 hover:text-cyan">
              {f}
            </span>
          ))}
        </div>
      </section>

      {/* Registration + venue */}
      <section className="relative mx-auto mt-8 max-w-[1600px] px-6 pb-28 md:px-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div className="border border-white/10 bg-ink-700/40 p-8 md:p-10">
            <div className="mb-6 flex items-center gap-3">
              <Ticket size={26} className="text-cyan" />
              <h2 className="font-head text-2xl font-bold uppercase tracking-tight text-white">{t("registration")}</h2>
            </div>
            <div className="flex flex-col divide-y divide-white/5">
              {COURSE_INFO.fees.map((f) => (
                <div key={f.label} className="flex items-center justify-between py-3">
                  <span className="font-body text-sm text-slate-300">{f.label}</span>
                  <span className="font-head text-xl font-bold text-cyan">{f.price}</span>
                </div>
              ))}
            </div>
            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{t("fee_includes")}</p>
            <ul className="mt-3 space-y-2">
              {COURSE_INFO.includes.map((inc) => (
                <li key={inc} className="flex items-start gap-2 font-body text-sm text-slate-400">
                  <CheckCircle size={16} weight="fill" className="mt-0.5 shrink-0 text-cyan/60" /> {inc}
                </li>
              ))}
            </ul>
            <a
              href={`mailto:giusi@tigicongress.com?subject=${encodeURIComponent("Wall Board Academy — Registration")}`}
              data-testid="register-btn"
              className="group mt-8 inline-flex items-center gap-3 bg-cyan px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink-900 transition-colors hover:bg-white"
            >
              {t("register_now")} <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-1" />
            </a>
          </div>

          <div className="relative min-h-[320px] overflow-hidden border border-white/10">
            <img src={OR_IMG} alt="Operating room" className="absolute inset-0 h-full w-full object-cover opacity-50" />
            <div className="absolute inset-0 bg-ink-900/50 mix-blend-multiply" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/20 to-transparent" />
            <div className="relative flex h-full flex-col justify-end gap-4 p-8 md:p-10">
              <div className="flex items-start gap-3">
                <MapPin size={22} className="mt-0.5 shrink-0 text-cyan" />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan">{t("venue")}</p>
                  <p className="mt-1 font-body text-sm text-slate-200">{COURSE_INFO.venue}</p>
                </div>
              </div>
              <div className="border-t border-white/10 pt-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{t("secretariat")}</p>
                <p className="mt-1 font-body text-sm text-slate-300">{COURSE_INFO.secretariat}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function InfoBlock({ icon: Icon, title, items }) {
  return (
    <div className="border border-white/10 bg-ink-700/30 p-8">
      <div className="mb-5 flex items-center gap-3">
        <Icon size={22} className="text-cyan" />
        <h3 className="font-head text-lg font-bold uppercase tracking-tight text-white">{title}</h3>
      </div>
      <ul className="space-y-3">
        {items.map((it) => (
          <li key={it} className="font-body text-sm leading-relaxed text-slate-400">{it}</li>
        ))}
      </ul>
    </div>
  );
}
